import { AxiosInstance, AxiosResponse } from "axios";
import { History } from "history";
import AlertMessageStore from "@/stores/AlertMessageStore";
import { failure, pending, Result } from "@/common/common";
import { makeAutoObservable } from "mobx";
import { noticeForShareholderUrl } from "@/utils/navigation";
import { FC, useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import { TFunction, TMessage } from "@/i18n/tt";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { Alert } from "@/components-v2/Alert";
import { DefaultLayout } from "@/containers";
import { useMessageStore, useRootStore, useTranslation } from "@/stores/RootStoreContext";

class ResolveNoticeForShareholderController {
  http: AxiosInstance;
  history: History;
  messageStore: AlertMessageStore;
  result: Result<void, TMessage> = pending();

  constructor(http: AxiosInstance, history: History, messageStore: AlertMessageStore) {
    makeAutoObservable(this, { http: false, history: false }, { autoBind: true });
    this.http = http;
    this.history = history;
    this.messageStore = messageStore;
  }

  *resolveNoticeForShareholderId(documentId: string) {
    try {
      const response: AxiosResponse = yield this.http.get<string>(
        `/shareholder-view/resolve-notice-for-shareholder-id/${documentId}`,
      );
      this.history.replace(noticeForShareholderUrl(response.data));
    } catch (e) {
      this.result = failure({ key: "noticeForShareholder.loadNoticeError" });
    }
  }
}

const ResolveNoticeForShareholderPage: FC<{ documentId: string }> = observer(({ documentId }) => {
  const { t } = useTranslation();
  const history = useRootStore().history;
  const { http } = useRootStore();
  const messageStore = useMessageStore();
  const ctrl = useMemo(
    () => new ResolveNoticeForShareholderController(http, history, messageStore),
    [http, history, messageStore],
  );

  useEffect(() => {
    ctrl.resolveNoticeForShareholderId(documentId);
  }, [ctrl, documentId]);

  function render(t: TFunction, state: Result<void, TMessage>) {
    switch (state.state) {
      case "pending":
        return <LoadingIndicator fullscreen />;
      case "failure":
        return <Alert type={"error"} message={t(state.error)} />;
      default:
        return undefined;
    }
  }

  return <DefaultLayout>{render(t, ctrl.result)}</DefaultLayout>;
});

export default ResolveNoticeForShareholderPage;
